// v5.44 — Foreplay harvest job modal (docs/superpowers/plans/2026-08-11-
// foreplay-brand-harvester.md). Brand directory search (cache-first; the
// "Search Foreplay" button is the ONLY thing that bills), filters, per-brand
// cap, and a live credit estimate against the account meter.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X, Search, Globe, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CachedBrand {
  id: number;
  foreplayId: string;
  name: string;
  avatarUrl: string | null;
  category: string | null;
  niches: string[] | null;
  websites: string[] | null;
  metaPageId: string | null;
  lastAnalytics: { activeCount: number; image: number; video: number; carousel: number } | null;
}

export interface ForeplayHarvestFilters {
  displayFormat: "image" | "video" | "carousel";
  live?: boolean;
  minDaysRunning?: number;
  order: "newest" | "oldest" | "longest_running" | "most_relevant";
  capPerBrand: number;
}

export function SeederForeplayJobModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (jobId: number) => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [domain, setDomain] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CachedBrand[]>([]);
  const [creating, setCreating] = useState(false);
  // v5.45 — default order is NEWEST: with a min-days floor it means "fresh
  // creative that already survived" — current-generation ad styles (Dan
  // compared against Foreplay's own UI; longest_running surfaced 550-day-old
  // visuals that read dated). Longest-running stays selectable for
  // evergreen digs.
  const [filters, setFilters] = useState<ForeplayHarvestFilters>({
    displayFormat: "image",
    minDaysRunning: 30,
    order: "newest",
    capPerBrand: 100,
  });
  const [liveOnly, setLiveOnly] = useState(false);
  const [analyticsBusy, setAnalyticsBusy] = useState<Set<string>>(new Set());
  const [nicheFilter, setNicheFilter] = useState("");

  const { data: usage } = useQuery<{ totalCredits: number; remainingCredits: number }>({
    queryKey: ["/api/admin/foreplay-harvest/usage"],
    queryFn: async () => {
      const res = await fetch("/api/admin/foreplay-harvest/usage");
      if (!res.ok) throw new Error("usage unavailable");
      return res.json();
    },
    staleTime: 60_000,
  });

  // Cache browse — free, refires as you type.
  const { data: cacheData, refetch: refetchCache } = useQuery<{ brands: CachedBrand[] }>({
    queryKey: ["/api/admin/foreplay-harvest/brands", query],
    queryFn: async () => {
      const res = await fetch(`/api/admin/foreplay-harvest/brands?query=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error("cache search failed");
      return res.json();
    },
  });
  const allResults = cacheData?.brands ?? [];
  // Distinct niche labels across the directory for the filter dropdown.
  const nicheOptions = Array.from(new Set(allResults.flatMap((b) => [b.category, ...(b.niches ?? [])]).filter((n): n is string => !!n && n !== "Service Business"))).sort();
  const results = nicheFilter
    ? allResults.filter((b) => b.category === nicheFilter || (b.niches ?? []).includes(nicheFilter))
    : allResults;

  const billedSearch = async (kind: "name" | "domain") => {
    setSearching(true);
    setError(null);
    try {
      const url = kind === "name"
        ? `/api/admin/foreplay-harvest/brands?query=${encodeURIComponent(query)}&fresh=1`
        : `/api/admin/foreplay-harvest/brands/domain?domain=${encodeURIComponent(domain)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error((await res.json()).message || "Search failed");
      await refetchCache();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/foreplay-harvest/usage"] });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  const fetchAnalytics = async (b: CachedBrand) => {
    setAnalyticsBusy((s) => new Set(s).add(b.foreplayId));
    try {
      const res = await fetch(`/api/admin/foreplay-harvest/brands/${b.foreplayId}/analytics`);
      if (res.ok) await refetchCache();
    } finally {
      setAnalyticsBusy((s) => { const n = new Set(s); n.delete(b.foreplayId); return n; });
    }
  };

  const createJob = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/foreplay-harvest/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brands: selected.map((b) => ({
            foreplayId: b.foreplayId,
            name: b.name,
            metaPageId: b.metaPageId,
            // v5.47 — Foreplay tags half its brands "Service Business" as a
            // catch-all (RYZE = ["Service Business","Food/Drink"]); prefer
            // the real niche when one exists.
            niche: (b.niches ?? []).find((n) => n && n !== "Service Business")
              || b.category
              || b.niches?.[0]
              || undefined,
          })),
          filters: { ...filters, live: liveOnly ? true : undefined },
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message || "Failed to create job");
      const job = await res.json();
      onCreated(job.id);
    } catch (e: any) {
      setError(e.message);
      setCreating(false);
    }
  };

  const estimate = selected.length * filters.capPerBrand;
  const overBudget = usage != null && estimate > usage.remainingCredits;
  const isSelected = (id: string) => selected.some((b) => b.foreplayId === id);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="foreplay-job-modal"
        className="bg-white rounded-xl max-w-4xl w-full h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#E5E2DC] flex items-center justify-between bg-white shrink-0">
          <div>
            <h3 className="font-semibold">New Foreplay Harvest</h3>
            <p className="text-xs text-[#746F66] mt-0.5">
              Pick brands → pull their top ads into Ad Review → approve keepers into Winning Ads.
              {usage && <span className="ml-2 font-medium text-gray-700">{usage.remainingCredits.toLocaleString()} / {usage.totalCredits.toLocaleString()} Foreplay credits left</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-[#746F66] hover:text-gray-800"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 space-y-4 flex flex-col flex-1 min-h-0">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-2.5 rounded shrink-0">{error}</div>
          )}

          {/* Search */}
          <div className="grid grid-cols-2 gap-3 shrink-0">
            <div className="flex gap-2">
              <input
                data-testid="input-foreplay-brand-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Brand name (browses our cache free)"
                className="flex-1 border border-[#E5E2DC] rounded px-3 py-1.5 text-sm"
              />
              <Button size="sm" variant="outline" disabled={!query.trim() || searching} onClick={() => billedSearch("name")}>
                {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                <span className="ml-1">Search Foreplay (~10cr)</span>
              </Button>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="input-foreplay-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="Or exact domain: ryzesuperfoods.com"
                className="flex-1 border border-[#E5E2DC] rounded px-3 py-1.5 text-sm"
              />
              <Button size="sm" variant="outline" disabled={!domain.trim() || searching} onClick={() => billedSearch("domain")}>
                <Globe className="h-3.5 w-3.5" /><span className="ml-1">Lookup</span>
              </Button>
            </div>
          </div>

          {/* Directory — the full cached brand index, browsing is free. */}
          <div className="flex items-center justify-between shrink-0">
            <div className="text-xs text-[#746F66]">
              {results.length.toLocaleString()} brand{results.length === 1 ? "" : "s"} in directory{nicheFilter ? ` · ${nicheFilter}` : ""} — browsing is free, typing filters instantly
            </div>
            <select
              data-testid="select-directory-niche"
              className="border border-[#E5E2DC] rounded px-2 py-1 text-xs"
              value={nicheFilter}
              onChange={(e) => setNicheFilter(e.target.value)}
            >
              <option value="">All niches</option>
              {nicheOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="border border-[#E5E2DC] rounded divide-y divide-gray-100 flex-1 min-h-0 overflow-y-auto">
            {results.length === 0 ? (
              <div className="p-4 text-sm text-[#746F66]">
                Nothing cached{query ? ` for “${query}”` : ""} yet — use “Search Foreplay” or a domain lookup (that's the only step that bills).
              </div>
            ) : results.map((b) => (
              <div key={b.foreplayId} data-testid={`foreplay-brand-row-${b.foreplayId}`} className="flex items-center gap-3 p-2.5">
                {b.avatarUrl
                  ? <img src={b.avatarUrl} alt="" className="h-8 w-8 rounded object-cover border border-[#E5E2DC]" onError={(e) => { (e.target as HTMLImageElement).style.visibility = "hidden"; }} />
                  : <div className="h-8 w-8 rounded bg-gray-100" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.name}</div>
                  <div className="text-[11px] text-[#746F66] truncate">
                    {[b.category, ...(b.niches ?? [])].filter(Boolean).join(" · ") || "—"}
                    {b.websites?.[0] ? ` · ${b.websites[0].replace(/^https?:\/\//, "")}` : ""}
                  </div>
                </div>
                {b.lastAnalytics ? (
                  <div className="text-[11px] text-[#746F66] text-right shrink-0">
                    <div className="font-medium">{b.lastAnalytics.activeCount.toLocaleString()} live</div>
                    <div>{b.lastAnalytics.image.toLocaleString()} img · {b.lastAnalytics.video.toLocaleString()} vid</div>
                  </div>
                ) : (
                  <button
                    className="text-[11px] text-indigo-600 hover:underline shrink-0 disabled:opacity-50"
                    disabled={analyticsBusy.has(b.foreplayId)}
                    onClick={() => fetchAnalytics(b)}
                  >
                    {analyticsBusy.has(b.foreplayId) ? "…" : "ad counts"}
                  </button>
                )}
                {isSelected(b.foreplayId) ? (
                  <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded shrink-0">Added</span>
                ) : (
                  <Button size="sm" variant="outline" className="shrink-0" data-testid={`button-add-brand-${b.foreplayId}`} onClick={() => setSelected((s) => [...s, b])}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Basket */}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 shrink-0 max-h-20 overflow-y-auto">
              {selected.map((b) => (
                <span key={b.foreplayId} className="inline-flex items-center gap-1.5 text-xs bg-gray-100 rounded-full pl-3 pr-1.5 py-1">
                  {b.name}
                  <button onClick={() => setSelected((s) => s.filter((x) => x.foreplayId !== b.foreplayId))} className="text-[#746F66] hover:text-red-600">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm shrink-0">
            <label className="block">
              <span className="text-xs text-[#746F66]">Format</span>
              <select
                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1.5 text-sm"
                value={filters.displayFormat}
                onChange={(e) => setFilters((f) => ({ ...f, displayFormat: e.target.value as ForeplayHarvestFilters["displayFormat"] }))}
              >
                <option value="image">Image</option>
                <option value="video">Video (stored as image stills — v1)</option>
                <option value="carousel">Carousel</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-[#746F66]">Min days running</span>
              <input
                type="number" min={0} max={3650}
                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1.5 text-sm"
                value={filters.minDaysRunning ?? 0}
                onChange={(e) => setFilters((f) => ({ ...f, minDaysRunning: Math.max(0, Number(e.target.value) || 0) }))}
              />
            </label>
            <label className="block">
              <span className="text-xs text-[#746F66]">Ads per brand (max 250)</span>
              <input
                type="number" min={1} max={250}
                data-testid="input-cap-per-brand"
                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1.5 text-sm"
                value={filters.capPerBrand}
                onChange={(e) => setFilters((f) => ({ ...f, capPerBrand: Math.min(250, Math.max(1, Number(e.target.value) || 1)) }))}
              />
            </label>
            <div className="flex flex-col justify-end gap-1.5">
              <label className="block">
                <span className="text-xs text-[#746F66]">Sort</span>
                <select
                  data-testid="select-harvest-order"
                  className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1.5 text-sm"
                  value={filters.order}
                  onChange={(e) => setFilters((f) => ({ ...f, order: e.target.value as ForeplayHarvestFilters["order"] }))}
                >
                  <option value="newest">Newest (fresh, survived min-days)</option>
                  <option value="longest_running">Longest running (evergreen)</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={liveOnly} onChange={(e) => setLiveOnly(e.target.checked)} />
                Currently live only
              </label>
            </div>
          </div>

          {/* Cost bar + create */}
          <div className="flex items-center justify-between border-t border-[#E5E2DC] pt-3 shrink-0">
            <div className="text-sm">
              <span className="font-medium">{selected.length} brand{selected.length === 1 ? "" : "s"} × {filters.capPerBrand}</span>
              <span className="text-[#746F66]"> = up to </span>
              <span className={`font-semibold ${overBudget ? "text-red-600" : "text-gray-900"}`}>{estimate.toLocaleString()} credits</span>
              {overBudget && <span className="text-red-600 text-xs ml-2">exceeds remaining credits</span>}
            </div>
            <Button
              data-testid="button-start-harvest"
              disabled={selected.length === 0 || creating || overBudget}
              onClick={createJob}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Start Harvest
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
